import * as Icons from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { DASH } from "@/constants/testIds";

/** A single dashboard stat / placeholder tile. If `to` is provided the whole card is a link. */
export function StatCard({ label, value, delta, icon = "Sparkles", tone = "navy", note, testKey, testId, index = 0, to = null }) {
  const Icon = Icons[icon] || Icons.Sparkles;
  const tones = {
    navy: "bg-[#101876]/5 text-[#101876]",
    green: "bg-[#329112]/10 text-[#329112]",
    slate: "bg-slate-100 text-slate-600",
    amber: "bg-amber-100 text-amber-700",
  };
  const clickable = to ? "cursor-pointer hover:border-[#101876]/40 hover:-translate-y-0.5" : "";
  const body = (
    <>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</div>
          <div className="mt-2 text-3xl font-black tracking-tight text-slate-900" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            {value}
          </div>
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      {(delta || note) && (
        <div className="mt-4 flex items-center gap-2 text-xs">
          {delta && <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">{delta}</span>}
          {note && <span className="text-slate-500">{note}</span>}
        </div>
      )}
    </>
  );
  const Wrapper = to ? Link : "div";
  const wrapperProps = to ? { to } : {};
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.05 }}
      className={`rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-all ${clickable}`}
      data-testid={testId || DASH.statCard(testKey)}
    >
      <Wrapper {...wrapperProps} className="block p-6">
        {body}
      </Wrapper>
    </motion.div>
  );
}

/** Placeholder card used for future-phase widgets. */
export function PlaceholderCard({ title, description, phase, icon = "Rocket", tone = "navy" }) {
  const Icon = Icons[icon] || Icons.Rocket;
  const tones = {
    navy: "bg-[#101876]/5 text-[#101876]",
    green: "bg-[#329112]/10 text-[#329112]",
    slate: "bg-slate-100 text-slate-600",
  };
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6">
      <div className="flex items-start gap-4">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-bold text-slate-900">{title}</h3>
            {phase && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                {phase}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-500 leading-relaxed">{description}</p>
        </div>
      </div>
    </div>
  );
}
